import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, FavoriteButton, Price, SellerBadge } from '@beautyburst/ui';
import { AVAILABILITY_LABEL_TEXT } from '@beautyburst/types';
import { semanticColor, space, typeStyle } from '@beautyburst/tokens';
import type { AvailabilityLabel, OwnershipType } from '@beautyburst/types';
import type { CustomerApiClient } from '../api-client.ts';
import { customerAnalytics } from '../analytics/client.ts';
import { CartAddFeedback, useCartAddFeedback } from '../cart-add-feedback.tsx';

export interface DetailMedia {
  mediaKey: string;
  variantId: string | null;
  altText: string;
}
export interface DetailVariant {
  variantId: string;
  label: string;
  attributes: Record<string, string>;
  quantityBounds: { minimum: number; maximum: number };
}
export interface DetailSeller {
  listingId: string;
  variantId: string;
  sellerDisplayName: string;
  sellerLabel: string;
  ownershipType: OwnershipType;
  storeName: string | null;
  priceCents: number;
  salePriceCents: number | null;
  saleActive: boolean;
  effectivePriceCents: number;
  stockStatus: 'IN_STOCK' | 'LOW' | 'CRITICAL' | 'OUT' | null;
  availableQuantity: number | null;
  pickupEligible: boolean;
  deliveryEligible: boolean;
  overnightEligible: boolean;
  availabilityLabel: AvailabilityLabel | null;
  distanceMiles: number | null;
  deliveryEstimateMinutes: null;
  membershipBenefit: null;
}
export interface ProductDetailData {
  product: {
    id: string;
    slug: string;
    name: string;
    description: string | null;
    brand: string | null;
    category_path: string | null;
  };
  media: readonly DetailMedia[];
  variantSelectors: readonly DetailVariant[];
  sellers: readonly DetailSeller[];
  reviews: { averageRating: number | null; reviewCount: number; reviews: readonly unknown[] };
}
export interface ProductDetailScreenProps {
  detail: ProductDetailData;
  client: Pick<CustomerApiClient, 'cart' | 'analytics'>;
  mediaUrlFor?: (mediaKey: string) => string | null;
}

export function ProductDetailScreen({ detail, client, mediaUrlFor }: ProductDetailScreenProps) {
  const [variantId, setVariantId] = useState(detail.variantSelectors[0]?.variantId ?? '');
  const [quantity, setQuantity] = useState(1);
  const [imageIndex, setImageIndex] = useState(0);
  const { addToBag, dismiss, feedback } = useCartAddFeedback(client);
  const variant =
    detail.variantSelectors.find((candidate) => candidate.variantId === variantId) ?? null;
  const seller = detail.sellers.find((candidate) => candidate.variantId === variantId) ?? null;
  const gallery = useMemo(
    () => detail.media.filter((image) => image.variantId === null || image.variantId === variantId),
    [detail.media, variantId],
  );
  const activeImage = gallery[imageIndex] ?? gallery[0] ?? null;
  const activeImageUrl =
    activeImage === null || mediaUrlFor === undefined ? null : mediaUrlFor(activeImage.mediaKey);
  const inStock =
    seller !== null &&
    seller.stockStatus !== null &&
    seller.stockStatus !== 'OUT' &&
    (seller.availableQuantity === null || seller.availableQuantity > 0);
  const maxQuantity = variant === null ? 1 : Math.max(1, variant.quantityBounds.maximum);
  const selectVariant = (nextId: string) => {
    setVariantId(nextId);
    setQuantity(1);
    setImageIndex(0);
  };
  const navigateGallery = (direction: 1 | -1) => {
    if (gallery.length > 0)
      setImageIndex((index) => (index + direction + gallery.length) % gallery.length);
  };
  const addSelectedItemToBag = async () => {
    if (seller === null || !inStock) return;
    if (await addToBag({ listingId: seller.listingId, quantity }))
      await (client.analytics ?? customerAnalytics())
        .track({
          name: 'cart.item_added',
          subjectKind: 'LISTING',
          subjectId: seller.listingId,
        })
        .catch(() => undefined);
  };
  useEffect(() => {
    void (client.analytics ?? customerAnalytics())
      .track({
        name: 'product.viewed',
        subjectKind: 'PRODUCT',
        subjectId: detail.product.id,
      })
      .catch(() => undefined);
  }, [client, detail.product.id]);
  return (
    <article aria-labelledby="product-heading" style={{ display: 'grid', gap: space[5] }}>
      <section aria-label="Product gallery">
        <div
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'ArrowRight') {
              event.preventDefault();
              navigateGallery(1);
            }
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              navigateGallery(-1);
            }
          }}
          aria-label={
            activeImage === null
              ? 'No product images'
              : `Image ${Math.min(imageIndex + 1, gallery.length)} of ${gallery.length}`
          }
        >
          {activeImageUrl === null || activeImage === null ? (
            <div
              role="img"
              aria-label={activeImage?.altText ?? `${detail.product.name} image unavailable`}
            />
          ) : (
            <img src={activeImageUrl} alt={activeImage.altText} />
          )}
        </div>
        <p aria-live="polite">
          {gallery.length === 0
            ? 'No product images'
            : `Image ${Math.min(imageIndex + 1, gallery.length)} of ${gallery.length}`}
        </p>
        <Button
          variant="secondary"
          onClick={() => navigateGallery(-1)}
          disabled={gallery.length < 2}
        >
          Previous image
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigateGallery(1)}
          disabled={gallery.length < 2}
        >
          Next image
        </Button>
      </section>
      <header>
        <p>{detail.product.brand ?? 'Beauty product'}</p>
        <h1
          id="product-heading"
          style={{ ...typeStyle('headingLg'), color: semanticColor.textPrimary, margin: 0 }}
        >
          {detail.product.name}
        </h1>
        <FavoriteButton favorited={false} subject={detail.product.name} />
        {seller === null ? (
          <p>Availability unavailable</p>
        ) : (
          <>
            <Price
              priceCents={seller.priceCents}
              saleActive={seller.saleActive}
              salePriceCents={seller.salePriceCents}
            />
            <SellerBadge
              retailerName={
                seller.ownershipType === 'BEAUTY_BURST_DIRECT' ? null : seller.sellerDisplayName
              }
            />
            {seller.storeName === null ? null : <p>{seller.storeName}</p>}
            {seller.distanceMiles === null ? (
              <p>Distance unavailable</p>
            ) : (
              <p>{seller.distanceMiles.toFixed(1)} mi away</p>
            )}
            {seller.availabilityLabel === null ? null : (
              <Badge variant="info">{AVAILABILITY_LABEL_TEXT[seller.availabilityLabel]}</Badge>
            )}
            {seller.pickupEligible ? <Badge variant="info">Pickup available</Badge> : null}
            {seller.deliveryEligible ? (
              <Badge variant="info">Delivery availability is confirmed at bag review</Badge>
            ) : null}
            {seller.overnightEligible ? (
              <p>Overnight eligibility requires server confirmation at bag review.</p>
            ) : null}
          </>
        )}
      </header>
      <section aria-labelledby="variants-heading">
        <h2 id="variants-heading" style={typeStyle('headingSm')}>
          Choose a variant
        </h2>
        {detail.variantSelectors.map((option) => (
          <label key={option.variantId}>
            <input
              type="radio"
              name="variant"
              checked={option.variantId === variantId}
              onChange={() => selectVariant(option.variantId)}
            />{' '}
            {option.label}
          </label>
        ))}
      </section>
      <label>
        Quantity
        <select
          aria-label="Quantity"
          value={quantity}
          onChange={(event) => setQuantity(Number(event.currentTarget.value))}
        >
          {Array.from({ length: maxQuantity }, (_, index) => index + 1).map((count) => (
            <option key={count} value={count}>
              {count}
            </option>
          ))}
        </select>
      </label>
      <p>{inStock ? 'In stock' : 'Out of stock'}</p>
      <Button
        variant="primary"
        disabled={!inStock || seller === null}
        onClick={() => void addSelectedItemToBag()}
      >
        Add to Bag
      </Button>
      <CartAddFeedback feedback={feedback} onDismiss={dismiss} />
      <section aria-labelledby="description-heading">
        <h2 id="description-heading" style={typeStyle('headingSm')}>
          Description
        </h2>
        <p>{detail.product.description ?? 'No product description has been provided.'}</p>
      </section>
      <section aria-labelledby="reviews-heading">
        <h2 id="reviews-heading" style={typeStyle('headingSm')}>
          Reviews
        </h2>
        <p>
          {detail.reviews.averageRating === null
            ? 'No reviews yet'
            : `${detail.reviews.averageRating.toFixed(1)} out of 5 from ${detail.reviews.reviewCount} reviews`}
        </p>
      </section>
      <section aria-labelledby="related-heading">
        <h2 id="related-heading" style={typeStyle('headingSm')}>
          Related products
        </h2>
        <p>Related products are shown when available.</p>
      </section>
    </article>
  );
}
